import React from 'react';
import clsx from 'clsx';
import {Button} from '@mui/material';
import {Alert} from '@netx/core-components/Alert';
import {useAutofillDetection} from '@netx/core-components/hooks/useAutofillDetection';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {
	loginClasses,
	getLoginUtilityClass,
	LoginClasses,
	LOGIN_CLASS_NAME,
} from './loginClasses';
import {LegacyFormTextField} from '@netx/core-components/Form/TextField';
import useForm from '@netx/core-components/Form/hooks/useForm';
import {LegacyForm as Form} from '@netx/core-components/Form/LegacyForm';
import {getInputFormObject} from '@netx/core-components/Form/util';
import {SubmitButton} from '@netx/core-components/SubmitButton';
import {useAuthenticate} from '../../hooks';
import {AuthFieldLabel, AuthActionLabel, AuthMessage} from '../../locales';
import type {LoginFormData} from '../../types';
import {canRequestPasswordReset, canSelfRegister} from '../../utils';
import {URL} from '@netx/core-next/url';

export type LoginOwnerState = WithClasses<LoginClasses, {}>;

const useUtilityClasses = (ownerState: LoginOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getLoginUtilityClass, classes);
};

const LoginRoot = styled('div', {
	name: LOGIN_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	// padding: theme.spacing(3, 6),
}));

const LoginHeader = styled('header', {
	name: LOGIN_CLASS_NAME,
	slot: 'Header',
})(({theme}) => ({
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	flex: '0 0 auto',
	padding: '24px 16px',
}));

const LoginContent = styled('div', {
	name: LOGIN_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	padding: theme.spacing(3, 6),
	width: '100%',
}));

const LoginFooter = styled('footer', {
	name: LOGIN_CLASS_NAME,
	slot: 'Footer',
})(({theme}) => ({
	flex: '0 0 auto',
	// padding: '24px 16px',
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'column',
	padding: '16px 24px 24px 24px',
	width: '100%',
}));

const LoginLogo = styled('div', {
	name: LOGIN_CLASS_NAME,
	slot: 'Logo',
})(({theme}) => ({
	position: 'relative',
	textAlign: 'center',
	height: 50,
	zIndex: 2,
	'& img': {
		maxHeight: 50,
		maxWidth: '100%',
		width: 'auto',
		height: 'auto',
	},
}));

const LoginTextField = styled(LegacyFormTextField, {
	name: LOGIN_CLASS_NAME,
	slot: 'Field',
})(({theme}) => ({
	'& + &': {
		marginTop: theme.spacing(2),
	},
}));

const LoginAlert = styled(Alert, {
	name: LOGIN_CLASS_NAME,
	slot: 'Alert',
})(({theme}) => ({
	justifyContent: 'center',
	position: 'relative',
	marginBottom: theme.spacing(2),
	width: '100%',
}));

export type LoginProps = WithClasses<
	LoginClasses,
	{
		onRequestPasswordReset?: () => void;
		onRequestRegistration?: () => void;
	}
>;

export const LoginPrimitive = ({
	classes: _classes,
	className,
	onRequestPasswordReset,
	onRequestRegistration,
	...props
}: LoginProps) => {
	const {authenticateUser, authenticated, loginMessage, samlServices} =
		useAuthenticate();

	const form = useForm<LoginFormData>(undefined, {
		onSubmit: authenticateUser,
	});
	const inputForm = getInputFormObject(form);

	const ownerState: LoginOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const usernameAutofillHack = useAutofillDetection();
	const passwordAutofillHack = useAutofillDetection();

	return (
		<LoginRoot className={clsx(classes.root, className)}>
			<LoginHeader>
				<LoginLogo>
					<img alt="logo" src={URL.logoLight} />
				</LoginLogo>
			</LoginHeader>

			{form.submissionError ||
			form.isSubmitting ||
			(loginMessage != null && loginMessage !== '') ||
			authenticated ? (
				<LoginAlert
					severity={
						authenticated ? 'success' : form.isSubmitting ? 'info' : 'error'
					}
				>
					{authenticated
						? AuthMessage.SIGNED_IN
						: form.isSubmitting
						? AuthMessage.SIGNING_IN
						: form.submissionError
						? form.submissionError
						: loginMessage}
				</LoginAlert>
			) : null}

			<Form
				form={form}
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				<LoginContent>
					<LoginTextField
						{...usernameAutofillHack({
							error: form.errors.login?.message,
							form: inputForm,
							name: 'login',
							label: AuthFieldLabel.login,
							required: true,
							variant: 'standard',
							value: form.values.login,
						})}
					/>
					<LoginTextField
						{...passwordAutofillHack({
							error: form.errors.password?.message,
							form: inputForm,
							name: 'password',
							label: AuthFieldLabel.password1,
							required: true,
							type: 'password',
							variant: 'standard',
							value: form.values.password,
						})}
					/>
				</LoginContent>

				<LoginFooter>
					<SubmitButton
						showWorking={form.isSubmitting}
						disabled={!form.isValid || authenticated || !form.hasChanged}
						label={AuthActionLabel.SIGN_IN}
					/>

					{onRequestPasswordReset && canRequestPasswordReset() ? (
						<Button
							style={{marginTop: 16}}
							variant="text"
							onClick={() => {
								onRequestPasswordReset();
							}}
						>
							{AuthActionLabel.FORGOT_PASSWORD}
						</Button>
					) : null}

					{onRequestRegistration && canSelfRegister() ? (
						<Button
							style={{marginTop: 8}}
							variant="text"
							onClick={() => {
								onRequestRegistration();
							}}
						>
							{AuthActionLabel.CREATE_NEW_ACCOUNT}
						</Button>
					) : null}

					{samlServices ? (
						<Button
							style={{marginTop: 16}}
							variant="outlined"
							onClick={() => {
								dispatcher.trigger(
									'auth:user:saml:navigate',
									samlServices.name,
								);
							}}
						>
							{samlServices.label}
						</Button>
					) : null}
				</LoginFooter>
			</Form>
		</LoginRoot>
	);
};

export const Login = LoginPrimitive;

export default Login;
